import React, { useEffect, useRef, useState } from 'react';
import { getDoctorByEmail, getPatientsByDoctor, getVitalSigns, getLatestTemperature, getVitalsStatsWide, updateDoctorPreferences, type VitalsStatsWideRow, type DoctorPreferences } from '../api/database';
import { authenticatedFetch } from '../api/authenticatedFetch';
import { apiUrl } from '../config/appConfig';

type PatientRow = {
  pat_id: string;
  first_name: string;
  last_name: string;
  dev_id: string;
  email?: string;
  doctor_notes?: string;
  amka?: string | null;
  latestVitals?: { heartRate?: number; breathingRate?: number; spo2?: number; temperature?: number; timestamp?: string };
  /** 7d stats (from vitals_stats_wide_vw) */
  statsVitals?: VitalsStatsWideRow | null;
  /** 24h stats (from vitals_stats_wide_last24h_vw), fetched when user toggles to 24h */
  statsVitals24h?: VitalsStatsWideRow | null;
};

type Props = { onNavigate: (page: any) => void; onLogout: () => void; isDemo?: boolean };

const patientsApiUrl = (path: string) => apiUrl(path);

const STATS_FETCH_TIMEOUT_MS = 15000;

const withTimeout = <T,>(promise: Promise<T>, ms: number, fallback: T): Promise<T> =>
  Promise.race([
    promise,
    new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);

const PatientsPage: React.FC<Props> = ({ onNavigate, onLogout, isDemo = false }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<PatientRow[]>([]);
  
  // Add Patient Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPatient, setNewPatient] = useState({
    deviceId: '',
    email: '',
    notes: ''
  });
  const [addPatientLoading, setAddPatientLoading] = useState(false);
  const [addPatientError, setAddPatientError] = useState<string | null>(null);

  // Edit Patient Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPatient, setEditingPatient] = useState<{
    pat_id: string;
    first_name: string;
    last_name: string;
    dev_id: string;
    email?: string;
    notes?: string;
    amka?: string | null;
  } | null>(null);
  const [editPatientLoading, setEditPatientLoading] = useState(false);
  const [editPatientError, setEditPatientError] = useState<string | null>(null);

  // Delete Patient Confirmation Modal State
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deletePatientLoading, setDeletePatientLoading] = useState(false);
  const [deletePatientError, setDeletePatientError] = useState<string | null>(null);

  // Hover state for patient cards
  const [hoveredPatientId, setHoveredPatientId] = useState<string | null>(null);
  // Stats time range per patient: 24h or 7d (default 7d)
  const [statsTimeRangeByPat, setStatsTimeRangeByPat] = useState<Record<string, '24h' | '7d'>>({});
  // Reorder: drag state (order is rows array; reset on logout since we don't persist yet)
  const [draggedPatId, setDraggedPatId] = useState<string | null>(null);
  const [dragOverPatId, setDragOverPatId] = useState<string | null>(null);
  const draggedPatIdRef = useRef<string | null>(null);
  const docIdRef = useRef<string | null>(null);
  const statsTimeRangeByPatRef = useRef<Record<string, '24h' | '7d'>>({});
  useEffect(() => { statsTimeRangeByPatRef.current = statsTimeRangeByPat; }, [statsTimeRangeByPat]);

  // Prefix so keys are not numeric (JS reorders numeric keys); still one object e.g. { "p_11": 7, "p_22": 7, "p_35": 24 }
  const PREFS_KEY_PREFIX = 'p_';
  const buildPreferencesFromRows = (currentRows: PatientRow[], rangeByPat: Record<string, '24h' | '7d'>): DoctorPreferences => {
    const prefs: DoctorPreferences = {};
    currentRows.forEach((r) => {
      prefs[PREFS_KEY_PREFIX + String(r.pat_id)] = rangeByPat[r.pat_id] === '24h' ? 24 : 7;
    });
    return prefs;
  };

  const persistPreferences = async (currentRows: PatientRow[], rangeByPat: Record<string, '24h' | '7d'>) => {
    const docId = docIdRef.current;
    if (!docId) return;
    const prefs = buildPreferencesFromRows(currentRows, rangeByPat);
    try {
      await updateDoctorPreferences(docId, prefs);
    } catch (e) {
      console.warn('Failed to persist doctor preferences:', e);
    }
  };

  const handleDragStart = (e: React.DragEvent, patId: string) => {
    e.dataTransfer.setData('text/plain', patId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.dropEffect = 'move';
    draggedPatIdRef.current = patId;
    setDraggedPatId(patId);
    e.stopPropagation();
  };

  const handleDragEnd = () => {
    draggedPatIdRef.current = null;
    setDraggedPatId(null);
    setDragOverPatId(null);
  };

  const handleDragEnter = (e: React.DragEvent, targetPatId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedPatId && draggedPatId !== targetPatId) setDragOverPatId(targetPatId);
  };

  const handleDragOver = (e: React.DragEvent, targetPatId: string) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (draggedPatId && draggedPatId !== targetPatId) setDragOverPatId(targetPatId);
  };

  const handleDragLeave = (e: React.DragEvent, targetPatId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains((e.relatedTarget as Node))) return;
    setDragOverPatId((id) => (id === targetPatId ? null : id));
  };

  const handleDrop = (e: React.DragEvent, targetPatId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverPatId(null);
    const draggedId = draggedPatIdRef.current || e.dataTransfer.getData('text/plain') || '';
    draggedPatIdRef.current = null;
    setDraggedPatId(null);
    if (!draggedId || draggedId === targetPatId) return;
    setRows((prevRows) => {
      const fromIndex = prevRows.findIndex((r) => r.pat_id === draggedId);
      const toIndex = prevRows.findIndex((r) => r.pat_id === targetPatId);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return prevRows;
      const next = [...prevRows];
      next[fromIndex] = next[toIndex];
      next[toIndex] = prevRows[fromIndex];
      persistPreferences(next, statsTimeRangeByPatRef.current);
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const email = localStorage.getItem('currentUserEmail') || '';
        if (!email) { setError('No logged-in user email'); setLoading(false); return; }
        const doc = await getDoctorByEmail(email);
        if (!doc || !doc.doc_id) { setError('Doctor not found'); setLoading(false); return; }
        if (cancelled) return;
        docIdRef.current = doc.doc_id;
        // API returns preferences as string (TEXT in DB). Use "p_" prefix on keys so JS keeps order (numeric keys get reordered).
        const prefsRaw = doc.preferences;
        let preferences: DoctorPreferences =
          typeof prefsRaw === 'string'
            ? (() => { try { return JSON.parse(prefsRaw) as DoctorPreferences; } catch { return {}; } })()
            : (prefsRaw && typeof prefsRaw === 'object' ? (prefsRaw as DoctorPreferences) : {});
        const pats = await getPatientsByDoctor(doc.doc_id);
        if (cancelled) return;

        // If preferences empty, initialize from fetched patients order with days = 7 (use p_ prefix so order is preserved)
        const prefsEmpty = !preferences || Object.keys(preferences).length === 0;
        if (prefsEmpty && pats.length > 0) {
          preferences = {};
          pats.forEach((p) => { preferences[PREFS_KEY_PREFIX + String(p.pat_id)] = 7; });
          void updateDoctorPreferences(doc.doc_id, preferences).catch(() => { /* non-fatal */ });
        }

        // p_ prefix so JS keeps key order (numeric-looking keys get reordered otherwise)
        const hasPrefixedKeys = Object.keys(preferences).some((k) => k.startsWith(PREFS_KEY_PREFIX));
        const orderKeys = hasPrefixedKeys ? Object.keys(preferences).filter((k) => k.startsWith(PREFS_KEY_PREFIX)) : Object.keys(preferences).filter((k) => k !== '_order');
        const patIdOrder = orderKeys.map((k) => (k.startsWith(PREFS_KEY_PREFIX) ? k.slice(PREFS_KEY_PREFIX.length) : k));
        const getDays = (patId: string) => (hasPrefixedKeys ? preferences[PREFS_KEY_PREFIX + String(patId)] : preferences[String(patId)]);

        // Sort by key order in preferences
        const sortedPats = [...pats].sort((a, b) => {
          const i = patIdOrder.indexOf(String(a.pat_id));
          const j = patIdOrder.indexOf(String(b.pat_id));
          if (i === -1 && j === -1) return 0;
          if (i === -1) return 1;
          if (j === -1) return -1;
          return i - j;
        });

        // Per-patient days from preferences (7 -> 7d, 24 -> 24h)
        const initialRangeByPat: Record<string, '24h' | '7d'> = {};
        sortedPats.forEach((p) => {
          const days = getDays(p.pat_id);
          initialRangeByPat[p.pat_id] = days === 24 ? '24h' : '7d';
        });
        setStatsTimeRangeByPat(initialRangeByPat);

        // Show the patient list immediately; load vitals/stats in the background.
        setRows(sortedPats.map((pat) => ({
          ...pat,
          statsVitals: null,
          statsVitals24h: undefined,
        })));
        setLoading(false);

        if (isDemo) {
          const patsWithVitals = await Promise.all(sortedPats.map(async (pat) => {
            try {
              const vitalsRows = await withTimeout(getVitalSigns(1, pat.pat_id), STATS_FETCH_TIMEOUT_MS, []);
              let temperatureValue: number | undefined;
              let tempTimestamp: string | undefined;
              try {
                const latestTemp = await withTimeout(getLatestTemperature(pat.pat_id), STATS_FETCH_TIMEOUT_MS, null);
                if (latestTemp && latestTemp.temp != null && latestTemp.temp !== -1) {
                  temperatureValue = latestTemp.temp;
                  tempTimestamp = latestTemp.timestamp;
                }
              } catch (tempErr) {
                console.warn(`Failed to fetch temperature for patient ${pat.pat_id}:`, tempErr);
              }
              if (Array.isArray(vitalsRows) && vitalsRows.length > 0) {
                const r: any = vitalsRows[0];
                const heartRate = r.heart_rate ?? r.heart_rate_bpm ?? r.pulse_rate ?? r.pulse_rate_bpm;
                const breathingRate = r.respiratory_rate;
                const spo2 = r.spo2 ?? r.spo2_bpm ?? r.spo2_bpm_ms ?? r.blood_oxygen_saturation;
                const timestamp = r.timestamp ?? r.time ?? r.created_at ?? r.createdAt ?? r.ts;
                return {
                  ...pat,
                  latestVitals: {
                    heartRate: typeof heartRate === 'number' && heartRate !== -1 ? heartRate : (heartRate != null && heartRate !== -1 ? Number(heartRate) : undefined),
                    breathingRate: typeof breathingRate === 'number' && breathingRate !== -1 ? breathingRate : (breathingRate != null && breathingRate !== -1 ? Number(breathingRate) : undefined),
                    spo2: typeof spo2 === 'number' && spo2 !== -1 ? spo2 : (spo2 != null && spo2 !== -1 ? Number(spo2) : undefined),
                    temperature: temperatureValue,
                    timestamp: timestamp ? new Date(timestamp).toLocaleString() : (tempTimestamp ? new Date(tempTimestamp).toLocaleString() : undefined)
                  }
                };
              }
              if (temperatureValue !== undefined) {
                return { ...pat, latestVitals: { temperature: temperatureValue, timestamp: tempTimestamp ? new Date(tempTimestamp).toLocaleString() : undefined } };
              }
              return pat;
            } catch (e) {
              console.warn(`Failed to fetch vitals for patient ${pat.pat_id}:`, e);
              return pat;
            }
          }));
          if (!cancelled) setRows(patsWithVitals);
        } else {
          const patsWithStats = await Promise.all(sortedPats.map(async (pat) => {
            try {
              const preferredRange = initialRangeByPat[pat.pat_id] ?? '7d';
              const [stats7d, stats24h] = await Promise.all([
                withTimeout(getVitalsStatsWide(pat.pat_id, '7d'), STATS_FETCH_TIMEOUT_MS, null),
                preferredRange === '24h'
                  ? withTimeout(getVitalsStatsWide(pat.pat_id, '24h'), STATS_FETCH_TIMEOUT_MS, null)
                  : Promise.resolve(undefined)
              ]);
              return {
                ...pat,
                statsVitals: stats7d ?? null,
                statsVitals24h: preferredRange === '24h' ? (stats24h ?? null) : undefined
              };
            } catch (e) {
              console.warn(`Failed to fetch vitals stats for patient ${pat.pat_id}:`, e);
              return { ...pat, statsVitals: null };
            }
          }));
          if (!cancelled) setRows(patsWithStats);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load patients');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isDemo]);

  const handleAddPatient = async () => {
    // Clear any previous errors
    setAddPatientError(null);
    
    // Validate required fields
    const email = newPatient.email?.trim();
    const deviceId = newPatient.deviceId?.trim();
    
    if (!email || !deviceId) {
      setAddPatientError('Please fill in all required fields (Email and Device ID)');
      return;
    }

    try {
      setAddPatientLoading(true);
      setAddPatientError(null);
      
      // Get current doctor's ID
      const doctorEmail = localStorage.getItem('currentUserEmail') || '';
      if (!doctorEmail) {
        setAddPatientError('No logged-in user email');
        return;
      }
      
      const doctor = await getDoctorByEmail(doctorEmail);
      if (!doctor || !doctor.doc_id) {
        setAddPatientError('Doctor not found');
        return;
      }
      
      // Make API call to update existing patient
      const requestBody = {
        email: newPatient.email,
        deviceId: newPatient.deviceId,
        notes: newPatient.notes,
        docId: doctor.doc_id
      };
      
      
      const response = await authenticatedFetch(patientsApiUrl('/patients/update-by-email'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update patient');
      }
      
      const updatedPatientData = await response.json();
      console.log('Patient updated successfully:', updatedPatientData);
      
      // Close modal and reset form
      setShowAddModal(false);
      setNewPatient({
        deviceId: '',
        email: '',
        notes: ''
      });
      
      // Refresh the patients list
      window.location.reload();
      
    } catch (error: any) {
      setAddPatientError(error?.message || 'Failed to update patient');
    } finally {
      setAddPatientLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setAddPatientError(null);
    setNewPatient({
      deviceId: '',
      email: '',
      notes: ''
    });
  };

  const handleEditPatient = (patient: any) => {
    console.log('Patient data for editing:', patient);
    setEditingPatient({
      pat_id: patient.pat_id,
      first_name: patient.first_name,
      last_name: patient.last_name,
      dev_id: patient.dev_id,
      email: patient.email || '',
      notes: patient.doctor_notes || '',
      amka: patient.amka || ''
    });
    setShowEditModal(true);
    setEditPatientError(null);
  };

  const handleUpdatePatient = async () => {
    if (!editingPatient || !editingPatient.dev_id || !editingPatient.dev_id.trim()) {
      setEditPatientError('Device ID is required');
      return;
    }

    try {
      setEditPatientLoading(true);
      setEditPatientError(null);

      const response = await authenticatedFetch(patientsApiUrl(`/patients/${editingPatient.pat_id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          deviceId: editingPatient.dev_id.trim(),
          notes: editingPatient.notes ?? ''
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update patient');
      }
      
      console.log('Patient updated successfully');
      
      // Close modal and reset form
      setShowEditModal(false);
      setEditingPatient(null);
      
      // Refresh the patients list
      window.location.reload();
      
    } catch (error: any) {
      setEditPatientError(error?.message || 'Failed to update patient');
    } finally {
      setEditPatientLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditPatientError(null);
    setEditingPatient(null);
  };

  const handleDeletePatient = () => {
    setShowDeleteConfirmation(true);
    setDeletePatientError(null);
  };

  const handleConfirmDelete = async () => {
    if (!editingPatient) return;

    try {
      setDeletePatientLoading(true);
      setDeletePatientError(null);
      
      const response = await authenticatedFetch(patientsApiUrl(`/patients/${editingPatient.pat_id}`), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete patient');
      }
      
      console.log('Patient deleted successfully');
      
      // Close both modals
      setShowDeleteConfirmation(false);
      setShowEditModal(false);
      setEditingPatient(null);
      
      // Refresh the patients list
      window.location.reload();
      
    } catch (error: any) {
      setDeletePatientError(error?.message || 'Failed to delete patient');
    } finally {
      setDeletePatientLoading(false);
    }
  };

  const handleCloseDeleteConfirmation = () => {
    setShowDeleteConfirmation(false);
    setDeletePatientError(null);
  };

  return (
    <>
      <header className="header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button className="hamburger-btn" onClick={() => setIsSidebarOpen(true)} aria-label="Open menu">☰</button>
          <h1>Overview</h1>
        </div>
      </header>

      <div className={`sidebar ${isSidebarOpen ? 'open' : ''}`} role="navigation" aria-label="Main menu">
        <ul className="sidebar-menu">
          <li
            onClick={() => {
              setIsSidebarOpen(false);
              onNavigate('patients');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <span className="mi">🏠</span><span>Overview</span>
          </li>
          <li onClick={() => { setIsSidebarOpen(false); onNavigate('history'); }}><span className="mi">🕒</span><span>History</span></li>
          <li onClick={() => { setIsSidebarOpen(false); onNavigate('timeline'); }}><span className="mi">📈</span><span>Timeline</span></li>
          <li onClick={() => { setIsSidebarOpen(false); onNavigate('settings'); }}><span className="mi">⚙️</span><span>Settings</span></li>
          <li onClick={() => { setIsSidebarOpen(false); onNavigate('consent'); }}><span className="mi">📝</span><span>Consent</span></li>
          <li onClick={() => { setIsSidebarOpen(false); onLogout(); }}><span className="mi">↪</span><span>Logout</span></li>
        </ul>
        <div style={{ marginTop: 16, padding: '0 24px' }}></div>
        <div className="sidebar-bottom">
          <div className="sidebar-brand"></div>
        </div>
      </div>
      <div className={`sidebar-backdrop ${isSidebarOpen ? 'show' : ''}`} onClick={() => setIsSidebarOpen(false)}></div>

      <div className="main-content" style={{ display: 'flex', columnGap: 16 }}>
        <div style={{ flex: 1 }}>
          {loading && <div>Loading patients…</div>}
          {error && <div style={{ color: 'red' }}>{error}</div>}
          {!loading && !error && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
              {rows.map((r) => {
                const isHovered = hoveredPatientId === r.pat_id;
                const isDragging = draggedPatId === r.pat_id;
                const isDropTarget = dragOverPatId === r.pat_id;
                const range = statsTimeRangeByPat[r.pat_id] ?? '7d';
                const statsForCard = range === '24h' ? r.statsVitals24h : r.statsVitals;
                const redCrit = statsForCard?.red_critical != null ? Number(statsForCard.red_critical) : 0;
                const yellowCrit = statsForCard?.yellow_critical != null ? Number(statsForCard.yellow_critical) : 0;
                const hasData = statsForCard != null && !isDemo;
                const cardColor = !hasData
                  ? { bg: '#fff', border: '#e0e0e0' }
                  : redCrit > 0
                    ? { bg: '#ffebee', border: '#ef9a9a' }
                    : yellowCrit > 0
                      ? { bg: '#fff3e0', border: '#ffcc80' }
                      : { bg: '#e8f5e9', border: '#a5d6a7' };
                const borderColor = isDropTarget ? '#1976d2' : cardColor.border;
                const boxShadow = isDropTarget
                  ? '0 0 0 3px rgba(25, 118, 210, 0.5), 0 4px 12px rgba(0,0,0,0.15)'
                  : isHovered ? '0 4px 12px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.05)';
                const cardDragEnter = (ev: React.DragEvent) => handleDragEnter(ev, r.pat_id);
                const cardDragOver = (ev: React.DragEvent) => handleDragOver(ev, r.pat_id);
                const cardDragLeave = (ev: React.DragEvent) => handleDragLeave(ev, r.pat_id);
                const cardDrop = (ev: React.DragEvent) => handleDrop(ev, r.pat_id);
                return (
                <div
                  key={r.pat_id}
                  onMouseEnter={() => setHoveredPatientId(r.pat_id)}
                  onMouseLeave={() => setHoveredPatientId(null)}
                  onDragEnter={cardDragEnter}
                  onDragOver={cardDragOver}
                  onDragLeave={cardDragLeave}
                  onDrop={cardDrop}
                  style={{
                    border: `2px solid ${borderColor}`,
                    borderRadius: 10,
                    padding: 16,
                    background: isDropTarget ? 'rgba(25, 118, 210, 0.08)' : cardColor.bg,
                    boxShadow,
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease',
                    transform: isHovered && !isDragging ? 'translateY(-2px)' : 'translateY(0)',
                    opacity: isDragging ? 0.5 : 1
                  }}
                >
                  {/* Reorder: drag handle — σύνρσιμο για αλλαγή σειράς */}
                  <div
                    draggable
                    onDragStart={(e) => handleDragStart(e, r.pat_id)}
                    onDragEnd={handleDragEnd}
                    title="Σύρσιμο για αλλαγή σειράς"
                    onClick={(ev) => ev.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      cursor: 'grab',
                      userSelect: 'none',
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '6px 10px',
                      background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                      border: '1px solid #90caf9',
                      borderRadius: 8,
                      color: '#1565c0',
                      fontSize: 12,
                      fontWeight: 600,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                  >
                    <span style={{ fontSize: 16, lineHeight: 1 }}>⋮⋮</span>
                  </div>
                  {/* [24h|7d] toggle per patient — same height as pencil, next to it, black; 24h fetches from last24h view */}
                  <div style={{ position: 'absolute', top: 8, right: 44, height: 32, display: 'flex', border: '1px solid #333', borderRadius: 6, overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={async () => {
                        const nextRange = { ...statsTimeRangeByPat, [r.pat_id]: '24h' as const };
                        setStatsTimeRangeByPat(() => nextRange);
                        persistPreferences(rows, nextRange);
                        if (r.statsVitals24h === undefined) {
                          const data = await getVitalsStatsWide(r.pat_id, '24h');
                          setRows(prev => prev.map(row => row.pat_id === r.pat_id ? { ...row, statsVitals24h: data ?? null } : row));
                        }
                      }}
                      style={{
                        height: 32,
                        padding: '0 8px',
                        fontSize: 11,
                        fontWeight: 600,
                        border: 'none',
                        cursor: 'pointer',
                        background: (statsTimeRangeByPat[r.pat_id] ?? '7d') === '24h' ? '#333' : '#fff',
                        color: (statsTimeRangeByPat[r.pat_id] ?? '7d') === '24h' ? '#fff' : '#333',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      24h
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const nextRange = { ...statsTimeRangeByPat, [r.pat_id]: '7d' as const };
                        setStatsTimeRangeByPat(() => nextRange);
                        persistPreferences(rows, nextRange);
                      }}
                      style={{
                        height: 32,
                        padding: '0 8px',
                        fontSize: 11,
                        fontWeight: 600,
                        border: 'none',
                        cursor: 'pointer',
                        background: (statsTimeRangeByPat[r.pat_id] ?? '7d') === '7d' ? '#333' : '#fff',
                        color: (statsTimeRangeByPat[r.pat_id] ?? '7d') === '7d' ? '#fff' : '#333',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      7d
                    </button>
                  </div>
                  {/* Edit Button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditPatient(r);
                    }}
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      background: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      cursor: 'pointer',
                      padding: '6px',
                      borderRadius: '6px',
                      color: '#666',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#e9ecef';
                      e.currentTarget.style.borderColor = '#007bff';
                      e.currentTarget.style.color = '#007bff';
                      e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                      e.currentTarget.style.borderColor = '#e0e0e0';
                      e.currentTarget.style.color = '#666';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                    }}
                    title="Edit patient"
                  >
                    ✏️
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      try { localStorage.setItem('selectedPatId', String(r.pat_id)); } catch {}
                      onNavigate('dashboard');
                      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
                    }}
                    onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); handleDragEnter(e, r.pat_id); }}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); handleDragOver(e, r.pat_id); }}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(e, r.pat_id); }}
                    style={{
                      textAlign: 'left',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      width: '100%',
                      padding: 0
                    }}
                  >
                    {/* Header: name (ellipsis) — toggle is absolute next to pencil; left padding for reorder strip */}
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, paddingLeft: 35, paddingRight: 100 }}>
                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: 20,
                          fontWeight: 700,
                          color: '#333'
                        }}
                        title={`${r.first_name} ${r.last_name}`}
                      >
                        {r.first_name} {r.last_name}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center', marginBottom: 12 }}>
                      <div style={{ fontSize: 12, color: '#666' }}>dev: {r.dev_id}</div>
                    </div>
                  
                  {/* Demo: only Median, Night Median, Latest time */}
                  {isDemo && (
                    <div style={{ background: '#f8f9fa', padding: '10px 12px', borderRadius: 8, border: '1px solid #e9ecef', marginBottom: 8 }}>
                      <div style={{ fontSize: 13, color: '#333', marginBottom: 4 }}> -</div>
                      <div style={{ fontSize: 12, color: '#555', marginTop: 6 }}>Latest time: {r.latestVitals?.timestamp ?? '-'}</div>
                    </div>
                  )}

                  {/* Real mode: stats from vitals_stats_wide_vw (7d) or vitals_stats_wide_last24h_vw (24h) — Median, min↓ max↑, invalid% */}
                  {!isDemo && (() => {
                    const boxStyle: React.CSSProperties = { background: '#f8f9fa', padding: '14px 18px', borderRadius: 8, textAlign: 'center', border: '1px solid #e9ecef', minHeight: 100, minWidth: 130 };
                    const formatNum = (v: number | null | undefined, decimals = 0) => v != null && !Number.isNaN(Number(v)) ? (decimals ? Number(v).toFixed(decimals) : Math.round(Number(v)).toString()) : null;
                    const formatPct = (v: number | null | undefined) => v != null && !Number.isNaN(Number(v)) ? `${Number(v).toFixed(1)}%` : '-';
                    const statFontSize = 13;
                    const numberFontSize = 16;
                    const StatBox = ({ label, median, nightMedian, min, max, invalidPct, unit, hideMax = false }: { label: string; median: number | null | undefined; nightMedian?: number | null; min: number | null | undefined; max: number | null | undefined; invalidPct: number | null | undefined; unit: string; hideMax?: boolean }) => (
                      <div style={boxStyle}>
                        <div style={{ fontSize: statFontSize, color: '#666', marginBottom: 6, fontWeight: 600 }}>{label}</div>
                        <div style={{ fontSize: numberFontSize, fontWeight: 700, color: '#333', marginBottom: 4 }}>
                          {formatNum(median, label === 'SPO2' ? 1 : 0) ?? '-'}{unit ? ` ${unit}` : ''}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, fontSize: numberFontSize - 1, fontWeight: 600, color: '#333', marginBottom: 4 }}>
                          <span title="Min">↓ {formatNum(min, label === 'SPO2' ? 1 : 0) ?? '-'}</span>
                          {!hideMax && <span title="Max">↑ {formatNum(max, label === 'SPO2' ? 1 : 0) ?? '-'}</span>}
                        </div>
                        <div style={{ fontSize: statFontSize, fontWeight: 700, color: '#333' }}>{formatPct(invalidPct)} out of range</div>
                      </div>
                    );
                    const range = statsTimeRangeByPat[r.pat_id] ?? '7d';
                    const s = range === '24h' ? r.statsVitals24h : r.statsVitals;
                    const formatCount = (v: number | string | null | undefined) => {
                      if (v == null) return '-';
                      const n = Number(v);
                      return !Number.isNaN(n) ? String(Math.round(n)) : '-';
                    };
                    const yellowCrit = formatCount(s?.yellow_critical);
                    const redCrit = formatCount(s?.red_critical);
                    return (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                          <StatBox label="HR" median={s?.median_heart_rate} nightMedian={undefined} min={s?.min_heart_rate} max={s?.max_heart_rate} invalidPct={s?.heart_rate_invalid_pct} unit="BPM" />
                          <StatBox label="HRV" median={s?.median_hrv} nightMedian={undefined} min={s?.min_hrv} max={s?.max_hrv} invalidPct={s?.hrv_invalid_pct} unit="ms" />
                          <StatBox label="SPO2" median={s?.median_spo2} nightMedian={undefined} min={s?.min_spo2} max={s?.max_spo2} invalidPct={s?.spo2_invalid_pct} unit="%" hideMax />
                          <StatBox label="RR" median={s?.median_respiratory_rate} nightMedian={undefined} min={s?.min_respiratory_rate} max={s?.max_respiratory_rate} invalidPct={s?.respiratory_rate_invalid_pct} unit="/min" />
                        </div>
                        <div style={{ borderTop: '1px solid #dee2e6', marginTop: 8, marginBottom: 10 }} />
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, fontSize: statFontSize, fontWeight: 700, color: '#333' }}>
                          <span>⚠️ Warnings: {yellowCrit}</span>
                          <span>🔴 Critical events: {redCrit}</span>
                        </div>
                      </>
                    );
                  })()}
                  </button>
                </div>
                );
              })}
              
              {/* Add New Patient Button */}
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                style={{
                  textAlign: 'center',
                  border: '2px dashed #e0e0e0',
                  borderRadius: 10,
                  padding: 16,
                  background: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '200px',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = '#007bff';
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = '#e0e0e0';
                  e.currentTarget.style.backgroundColor = '#fff';
                }}
              >
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  border: '2px solid #007bff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '12px',
                  fontSize: '24px',
                  color: '#007bff',
                  fontWeight: 'bold'
                }}>
                  +
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Click to assign patient to doctor
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add Patient Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#333' }}>
                Assign Patient to Doctor
              </h2>
              <button
                onClick={handleCloseModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '4px'
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleAddPatient(); }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333' }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={newPatient.email}
                  onChange={(e) => setNewPatient({ ...newPatient, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                  placeholder="Enter patient email address"
                  required
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333' }}>
                  Device ID *
                </label>
                <input
                  type="text"
                  value={newPatient.deviceId}
                  onChange={(e) => setNewPatient({ ...newPatient, deviceId: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                  placeholder="Enter device ID"
                  required
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333' }}>
                  Notes (Optional)
                </label>
                <textarea
                  value={newPatient.notes}
                  onChange={(e) => setNewPatient({ ...newPatient, notes: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '16px',
                    outline: 'none',
                    resize: 'vertical',
                    minHeight: '80px'
                  }}
                  placeholder="Enter any additional notes about the patient"
                />
              </div>

              {addPatientError && (
                <div style={{ color: '#d32f2f', marginBottom: '16px', fontSize: '14px' }}>
                  {addPatientError}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{
                    padding: '12px 24px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    background: 'white',
                    color: '#333',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addPatientLoading}
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: '6px',
                    background: addPatientLoading ? '#ccc' : '#007bff',
                    color: 'white',
                    cursor: addPatientLoading ? 'not-allowed' : 'pointer',
                    fontSize: '16px'
                  }}
                >
                  {addPatientLoading ? 'Assigning...' : 'Assign Patient'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Patient Modal */}
      {showEditModal && editingPatient && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#333' }}>
                Edit Patient
              </h2>
              <button
                onClick={handleCloseEditModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '4px'
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleUpdatePatient(); }}>
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
                <div style={{ marginBottom: '8px', fontSize: '15px', color: '#666' }}>Patient (read-only)</div>
                <div style={{ fontWeight: '600', color: '#333', fontSize: '18px' }}>
                  {editingPatient.first_name} {editingPatient.last_name}
                </div>
                {editingPatient.email && (
                  <div style={{ fontSize: '16px', color: '#555', marginTop: '4px' }}>{editingPatient.email}</div>
                )}
                {editingPatient.amka && (
                  <div style={{ fontSize: '15px', color: '#666', marginTop: '2px' }}>AMKA: {editingPatient.amka}</div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333' }}>
                  Device ID *
                </label>
                <input
                  type="text"
                  value={editingPatient.dev_id}
                  onChange={(e) => setEditingPatient({ ...editingPatient, dev_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                  placeholder="Enter device ID"
                  required
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333' }}>
                  Notes
                </label>
                <textarea
                  value={editingPatient.notes || ''}
                  onChange={(e) => setEditingPatient({ ...editingPatient, notes: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '16px',
                    outline: 'none',
                    resize: 'vertical',
                    minHeight: '80px'
                  }}
                  placeholder="Enter any additional notes about the patient"
                />
              </div>

              {editPatientError && (
                <div style={{ color: '#d32f2f', marginBottom: '16px', fontSize: '14px' }}>
                  {editPatientError}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
                <button
                  type="button"
                  onClick={handleDeletePatient}
                  style={{
                    padding: '12px 24px',
                    border: '1px solid #dc3545',
                    borderRadius: '6px',
                    background: 'white',
                    color: '#dc3545',
                    cursor: 'pointer',
                    fontSize: '16px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#dc3545';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.color = '#dc3545';
                  }}
                >
                  Delete
                </button>
                
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={handleCloseEditModal}
                    style={{
                      padding: '12px 24px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      background: 'white',
                      color: '#333',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editPatientLoading}
                    style={{
                      padding: '12px 24px',
                      border: 'none',
                      borderRadius: '6px',
                      background: editPatientLoading ? '#ccc' : '#007bff',
                      color: 'white',
                      cursor: editPatientLoading ? 'not-allowed' : 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    {editPatientLoading ? 'Updating...' : 'Update Patient'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && editingPatient && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '400px',
            maxWidth: '90vw',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                Delete Patient
              </h2>
              <button
                onClick={handleCloseDeleteConfirmation}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '4px'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <p style={{ margin: 0, fontSize: '16px', color: '#333', lineHeight: '1.5' }}>
                Are you sure you want to delete <strong>{editingPatient.first_name} {editingPatient.last_name}</strong>?
              </p>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>
                This action cannot be undone and will permanently remove all patient data.
              </p>
            </div>

            {deletePatientError && (
              <div style={{ color: '#d32f2f', marginBottom: '16px', fontSize: '14px' }}>
                {deletePatientError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleCloseDeleteConfirmation}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#333',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deletePatientLoading}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  background: deletePatientLoading ? '#ccc' : '#dc3545',
                  color: 'white',
                  cursor: deletePatientLoading ? 'not-allowed' : 'pointer',
                  fontSize: '16px'
                }}
              >
                {deletePatientLoading ? 'Deleting...' : 'Delete Patient'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PatientsPage;


